// EVA-289: the Exit Price Assumptions tab autosaves, so a response can land
// while the user is still typing. Rehydrating the whole form from that response
// would undo every keystroke made while the request was in flight.
//
// This module is the pure half of that: the request payload, and the one piece
// of the response that has to come back into local state.

import { FIXED_AMOUNT, CAPITAL_DEDUCTION_KEYS } from './formState.js'
import { parseDecimalInput, parseFixedAmountInput } from '../utils/decimalInput.js'

const CAPITAL_DEDUCTION_PARAMS = {
  void_deduction: 'deduct_void_deduction',
  rent_free_deduction: 'deduct_rent_free_deduction',
  tenant_improvements: 'deduct_tenant_improvements',
  leasing_commissions: 'deduct_leasing_commissions',
  capital_expenses: 'deduct_capital_expenses'
}

// The whole tab in one request: the assumptions and the schedule share a
// transaction server-side, so a rejected row can never leave the exit rate saved
// on its own. It lives here rather than in the component because the unload
// handler sends it too, and because the Fixed Amount coercion is worth pinning —
// that input is read with its grouping commas, the percentage ones are not.
//
// A trap for anyone tempted to make the seeded blank row purely client-side by
// filtering fully blank rows out here: `flushSave` takes `submittedKeys` from
// `formRef.current.rows`, so filtering only in this function makes the two
// lengths disagree, `mergeSavedRowIds` refuses the merge, the row never learns
// its id and every save re-creates it. Filter once and derive `submittedKeys`
// from the same filtered array, or not at all.
export function buildSavePayload(form, requestId) {
  const payload = {
    request_id: requestId,
    exit_rate_percent: parseDecimalInput(form.exitRatePercent),
    exit_date_basis: form.exitDateBasis,
    // A blank date input is an empty string; the column is nullable and the
    // difference matters, so it goes out as null rather than "".
    specified_exit_date: form.specifiedExitDate || null,
    noi_calculation_method: form.noiCalculationMethod,
    manual_noi_to_capitalise: parseFixedAmountInput(form.manualNoiToCapitalise),
    add_back_void_deduction: Boolean(form.addBackVoidDeduction),
    add_back_rent_free_deduction: Boolean(form.addBackRentFreeDeduction),
    sale_deductions: form.rows.map((row) => ({
      id: row.id,
      name: row.name,
      deduction_type: row.deductionType,
      amount: row.inputMethod === FIXED_AMOUNT
        ? parseFixedAmountInput(row.amount)
        : parseDecimalInput(row.amount),
      input_method: row.inputMethod
    }))
  }

  CAPITAL_DEDUCTION_KEYS.forEach((key) => {
    payload[CAPITAL_DEDUCTION_PARAMS[key]] = Boolean(form.capitalDeductions[key])
  })

  return payload
}

// Exactly one thing in the response cannot be known by the client: the id the
// server assigned to a row it just created. Without merging it back the next
// autosave sends that row as new again, so `ReplaceAll` destroys the record and
// creates another — churning the id and the audit trail on every single edit.
// Merging anything else would fight the user for the input.
//
// The match is positional. `SaleDeductions::ReplaceAll` writes `position` from
// the submitted array index and the serializer sorts by `[position, id]`, so the
// Nth saved row is the Nth submitted row. `submittedKeys` freezes that mapping
// onto the local row keys captured when the request went out, so rows added or
// removed since then fall outside the merge instead of being misassigned.
export function mergeSavedRowIds(rows, submittedKeys, savedRows) {
  const saved = Array.isArray(savedRows) ? savedRows : []

  // That contract only holds while we are the sole writer. `ReplaceAll#commit!`
  // reads the schedule back after its transaction commits, so a second tab
  // saving at the same moment can make the response describe more rows than we
  // sent — and then `saved[0]` is the other client's record, not ours.
  // Repointing a local row at it would make our next autosave overwrite their
  // row and destroy our own, with nothing shown. Decline the merge instead: the
  // row is re-created on the next save, and the other tab's broadcast rehydrates
  // this one as soon as it is clean.
  if (saved.length !== submittedKeys.length) return rows

  const idByKey = new Map()
  submittedKeys.forEach((key, index) => {
    const id = saved[index]?.id
    if (id !== null && id !== undefined) idByKey.set(key, id)
  })

  if (idByKey.size === 0) return rows

  let changed = false
  const merged = rows.map((row) => {
    // Only fills a gap, never overwrites: a row that already carries an id is
    // already correct, and leaving it alone means a desynchronised response
    // cannot repoint an existing row at another row's record.
    if (row.id !== null && row.id !== undefined) return row

    const id = idByKey.get(row.key)
    if (id === undefined) return row

    changed = true
    return { ...row, id }
  })

  // Returning the same array when nothing was assigned keeps the caller from
  // re-rendering the table on every autosave that only touched an assumption.
  return changed ? merged : rows
}
